import client from "../client";
import { handleExtension } from "../../common/GlobalFunctions";
import { DataSelectType } from "../../models/DataSelectType";
import { TicketInterface } from "../../models/TicketModule/Ticket";
import dayjs from "dayjs";

class TicketService {
  async GetRecurringTickets(page: number, pageSize: number): Promise<TicketInterface[]> {
    try {
      let url = `/api/Tickets/Get`;

      let form: any = {
        page,
        pageSize,
        isRecurring: true,
      };

      const { data } = await client.post<TicketInterface[]>(url, form);

      return data;
    } catch (error) {
      throw error;
    }
  }
  async GetAll(page: number, pageSize: number) {
    try {
      let url = `/api/Tickets`;
      if (page > 0 && pageSize > 0) {
        url += `?page=${page}&pageSize=${pageSize}`;
      }
      const { data } = await client.get<TicketInterface[]>(url);
      return data;
    } catch (error) {
      throw error;
    }
  }

  async GetByFilter(
    page: number,
    pageSize: number,
    statusId: string | null,
    priorityId: string | null,
    filters?: DataSelectType[],
    startDate?: string,
    endDate?: string
  ): Promise<TicketInterface[]> {
    try {
      let url = `/api/Tickets/Get`;

      let form: any = {
        page,
        pageSize,
      };

      if (statusId) form.statusId = statusId;
      if (priorityId) form.priorityId = priorityId;
      if (startDate) form.startDate = startDate;
      if (endDate) form.endDate = endDate;

      if (filters && filters.length > 0) {
        filters.forEach((item) => {
          let key;

          if (item.field === "YearId") {
            key = "Year";
          } else if (item.field === "TicketsId") {
            key = "MasterTicket";
          } else {
            key = item.field;
          }

          form[key] = item.field === "TicketsId" ? Number(item.value) : item.value;
        });
      }

      const { data } = await client.post<TicketInterface[]>(url, form);

      return data;
    } catch (error) {
      throw error;
    }
  }
  async GetById(id: string) {
    try {
      let url = `/api/Tickets/getbyid/${id}`;

      const { data } = await client.get<TicketInterface>(url);

      return data;
    } catch (error) {
      throw error;
    }
  }

  async Save(ticket: TicketInterface) {
    try {
      const url = "/api/Tickets";

      const formData = new FormData();

      for (const key in ticket) {
        if (key !== "formImages") {
          let value = (ticket as any)[key];

          if (key === "masterTicketId" && (value === undefined || value === null)) {
            continue;
          }

          if (key === "dueDate" && value instanceof Date) {
            value = dayjs(value).format("YYYY-MM-DDTHH:mm:ss");
          }

          formData.append(key, value);
        }
      }

      if (ticket.formImages && Array.isArray(ticket.formImages)) {
        for (let i = 0; i < ticket.formImages.length; i++) {
          const base64Image = ticket.formImages[i];
          const blob = await fetch(base64Image).then((res) => res.blob());
          const mimeType = base64Image.match(/data:(.*?);base64/)?.[1];
          let extension = "png";
          if (mimeType) extension = handleExtension(mimeType);

          formData.append("formImages", blob, `image${i}.${extension}`);
        }
      }

      const { data } = await client.post<TicketInterface>(url, formData);
      return data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async UpdateStatus(ticketId: string, statusId: string) {
    try {
      let url = `/api/Tickets/SetStatus/${ticketId}`;

      const { data } = await client.post<TicketInterface>(url, { statusId });
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async UpdateAssignTo(ticketId: string, assignedToId: string) {
    try {
      let url = `/api/Tickets/SetAssignedTo/${ticketId}`;

      const { data } = await client.post<TicketInterface>(url, {
        assignedToId,
      });

      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
  async Update(id: string, ticket: TicketInterface) {
    try {
      const url = `/api/Tickets/update/${id}`;

      const formData = new FormData();

      for (const key in ticket) {
        if (key === "formImages" || key === "images") continue;

        let value = (ticket as any)[key];

        if (value === undefined || value === null) continue;

        if (typeof value === "string" && value.trim() === "") continue;

        if (typeof value === "object" && !(value instanceof Date)) continue;

        if (key === "recurringPeriod" && (value === 0 || value === "0")) continue;

        if (key === "masterTicketId" && (value === undefined || value === null)) continue;

        if (key === "dueDate") {
          const d = value instanceof Date ? value : new Date(value);
          if (!isNaN(d.getTime())) {
            value = dayjs(d).format("YYYY-MM-DDTHH:mm:ss");
          } else {
            continue;
          }
        }

        if (typeof value === "boolean") value = value ? "true" : "false";
        if (typeof value === "number") value = String(value);

        formData.append(key, value);
      }

      ticket.images &&
        ticket.images.forEach((image: any, index: number) => {
          formData.append(`images[${index}][imageUrl]`, image.imageUrl);
        });

      if (ticket.formImages && Array.isArray(ticket.formImages)) {
        for (let i = 0; i < ticket.formImages.length; i++) {
          const base64Image = ticket.formImages[i];
          const blob = await fetch(base64Image).then((res) => res.blob());
          const mimeType = base64Image.match(/data:(.*?);base64/)?.[1];

          let extension = "png";
          if (mimeType) extension = handleExtension(mimeType);

          formData.append("formImages", blob, `image${i}.${extension}`);
        }
      }

      const { data } = await client.post<TicketInterface>(url, formData);

      return data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async Remove(id: string) {
    try {
      let url = `api/Tickets/remove/${id}`;
      await client.post<TicketInterface>(url, { url: "" });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}

export default new TicketService();
